import { type PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

export interface UseMousePreviewOptions {
  /**
   * How long (ms) the mouse has to rest on the target before the preview opens
   */
  openDelay?: number;

  /**
   * How long (ms) to wait after the mouse leaves before closing, giving the
   * user time to move from the target into the preview
   */
  closeDelay?: number;
}

/**
 * Manages show/hide state for a hover preview that should only respond to
 * an actual mouse (incl. trackpads), never to touch or pen input.
 *
 * Detection is done per-event via `PointerEvent.pointerType` rather than
 * via media queries, since `(hover: hover)` only describes the *primary*
 * input and is wrong on hybrid devices. It also avoids the emulated mouse
 * events mobile browsers fire after a tap.
 *
 * Spread `targetHandlers` on the element being hovered and `overlayHandlers`
 * on the preview itself, so moving into the preview keeps it open.
 */
export const useMousePreview = ({ openDelay = 500, closeDelay = 200 }: UseMousePreviewOptions = {}) => {
  const [show, setShow] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    openTimer.current = undefined;
    closeTimer.current = undefined;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const hide = useCallback(() => {
    clearTimers();
    setShow(false);
  }, [clearTimers]);

  useEffect(() => {
    if (!show) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [show, hide]);

  const cancelClose = useCallback((e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;

    clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
  }, []);

  const onTargetPointerEnter = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;

      cancelClose(e);
      if (openTimer.current) return;
      openTimer.current = setTimeout(() => {
        openTimer.current = undefined;
        setShow(true);
      }, openDelay);
    },
    [cancelClose, openDelay]
  );

  const onPointerLeave = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;

      clearTimeout(openTimer.current);
      openTimer.current = undefined;
      clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => {
        closeTimer.current = undefined;
        setShow(false);
      }, closeDelay);
    },
    [closeDelay]
  );

  return {
    show,
    hide,
    targetHandlers: {
      onPointerEnter: onTargetPointerEnter,
      onPointerLeave,
      onPointerDown: hide,
    },
    overlayHandlers: {
      onPointerEnter: cancelClose,
      onPointerLeave,
    },
  };
};
